import { FC, useMemo } from 'react';

import CountUp from 'react-countup';

import { EMetric, metricDictionary, StatsResult } from 'api/useAnalytics';
import Decimal from 'decimal.js';
import {
  BaseBreakdownValue,
  EStatisticType
} from 'views/Statistics/StatisticsView.types';

import Typography from 'components/Typography/Typography';

import { AnalyticsInfoTooltip } from '../AnalyticsInfoTooltip/AnalyticsInfoTooltip';
import { getDecimalPlaces } from '../utils';

import { PercentageChange } from './PercentageChange';

type AnalyticsBoxHeaderProps = {
  stat?: StatsResult;
  metricName: EMetric;
  breakdownValues?: BaseBreakdownValue[];
  isBreakdown?: boolean;
  shouldHideBreakdown?: boolean;
};

const NO_DATA_VALUE = 'N/A';

export const AnalyticsBoxHeader: FC<AnalyticsBoxHeaderProps> = ({
  stat,
  metricName,
  breakdownValues = [],
  isBreakdown = false,
  shouldHideBreakdown = false
}) => {
  const metricMetaData = metricDictionary[metricName];
  const metricType = metricMetaData?.type ?? EStatisticType.NUMBER;
  const currentValue = useMemo(() => {
    if (metricType === EStatisticType.AMOUNT_AVERAGE) {
      const decimalValue = new Decimal(stat?.currentValue ?? 0);
      return decimalValue.div(100).toNumber();
    }
    return stat?.currentValue;
  }, [stat, metricType]);

  if (metricMetaData === undefined) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-[10px]">
        <Typography className="text-base-cardForeground font-semibold">
          {metricMetaData.title}
        </Typography>
        <AnalyticsInfoTooltip>
          {metricMetaData.description}
        </AnalyticsInfoTooltip>
      </div>
      {!shouldHideBreakdown && (
        <Typography tag="h2" className="text-base-cardForeground mt-3 mb-1.5">
          {currentValue ? (
            <CountUp
              prefix={
                metricType === EStatisticType.CURRENCY ||
                metricType === EStatisticType.AMOUNT_AVERAGE
                  ? '$'
                  : ''
              }
              suffix={metricType === EStatisticType.PERCENTAGE ? '%' : ''}
              decimal="."
              decimals={getDecimalPlaces(currentValue, metricType)}
              separator=","
              duration={1}
              end={currentValue}
            />
          ) : (isBreakdown && breakdownValues.length === 0) ||
            (!isBreakdown && !currentValue) ? (
            NO_DATA_VALUE
          ) : null}
        </Typography>
      )}
      {!isBreakdown && (
        <PercentageChange changePercentage={stat?.changePercentage} />
      )}
    </div>
  );
};
